import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import PageLayout from "../../../components/layout/PageLayout";
import VPView from "./views/VPView";
import DirectorView from "./views/DirectorView";
import MUMView from "./views/MUMView";
import StoreView from "./views/StoreView";
import "./WindowReadinessResults.css";

const WindowReadinessResults = () => {
  console.log("🔵 WindowReadinessResults component mounted");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Report dates and selection
  const [reportDates, setReportDates] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState(null);

  // View selection
  const [currentView, setCurrentView] = useState("vp"); // vp, director, mum, store

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    directors: [],
    mums: [],
    stores: [],
  });

  // Selected filters
  const [selectedDirector, setSelectedDirector] = useState("");
  const [selectedMUM, setSelectedMUM] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  // View data
  const [viewData, setViewData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  // API Base URL
  const getAPIBase = () => {
    const hostname = window.location.hostname;
    const runningLocally = hostname === "localhost" || hostname === "127.0.0.1";
    const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;

    // If deployed (not localhost), use the current hostname with HTTPS
    if (!runningLocally) {
      return `https://${hostname}`;
    }

    // Running locally - use environment-specific URLs
    switch (env) {
      case "staging":
        return "https://staging.cafuamanagement.com";
      case "production":
        return "https://peopleplan.cafuamanagement.com";
      default:
        return "http://localhost:5000";
    }
  };

  // Authentication check
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const { exp } = jwtDecode(token);
      if (exp < Date.now() / 1000) {
        localStorage.removeItem("authToken");
        navigate("/");
      }
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("authToken");
      navigate("/");
    }
  }, [navigate]);

  // Fetch available report dates
  useEffect(() => {
    console.log("🔵 Fetching report dates useEffect triggered");
    const fetchReportDates = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const apiBase = getAPIBase();
        const url = `${apiBase}/api/window-readiness/report-dates`;
        console.log("🔵 Fetching from:", url);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("🔵 Response status:", response.status);

        if (!response.ok) {
          throw new Error("Failed to fetch report dates");
        }

        const dates = await response.json();
        console.log("🔵 Received dates:", dates);
        setReportDates(dates);

        // Auto-select most recent upload
        if (dates.length > 0) {
          setSelectedUploadId(dates[0].upload_id);
          console.log("🔵 Auto-selected upload_id:", dates[0].upload_id);
        } else {
          console.log("⚠️ No report dates found");
        }
      } catch (err) {
        console.error("❌ Error fetching report dates:", err);
        setError("Failed to load report dates. Please try again.");
      } finally {
        setLoading(false);
        console.log("🔵 Loading state set to false");
      }
    };

    fetchReportDates();
  }, []);

  // Fetch filter options when upload changes
  useEffect(() => {
    if (!selectedUploadId) return;

    const fetchFilterOptions = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(
          `${getAPIBase()}/api/window-readiness/filter-options/${selectedUploadId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch filter options");
        }

        const options = await response.json();
        setFilterOptions(options);

        // Reset selected filters
        setSelectedDirector("");
        setSelectedMUM("");
        setSelectedStore("");
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    };

    fetchFilterOptions();
  }, [selectedUploadId]);

  // Fetch view data when upload or view changes
  useEffect(() => {
    if (!selectedUploadId) return;

    const fetchViewData = async () => {
      try {
        setLoadingData(true);
        const token = localStorage.getItem("authToken");
        let url = "";

        switch (currentView) {
          case "vp":
            url = `${getAPIBase()}/api/window-readiness/vp/${selectedUploadId}`;
            break;
          case "director":
            if (!selectedDirector) {
              setViewData(null);
              setLoadingData(false);
              return;
            }
            url = `${getAPIBase()}/api/window-readiness/director/${selectedUploadId}/${encodeURIComponent(
              selectedDirector
            )}`;
            break;
          case "mum":
            if (!selectedMUM) {
              setViewData(null);
              setLoadingData(false);
              return;
            }
            url = `${getAPIBase()}/api/window-readiness/mum/${selectedUploadId}/${encodeURIComponent(
              selectedMUM
            )}`;
            break;
          case "store":
            if (!selectedStore) {
              setViewData(null);
              setLoadingData(false);
              return;
            }
            url = `${getAPIBase()}/api/window-readiness/store/${selectedUploadId}/${encodeURIComponent(
              selectedStore
            )}`;
            break;
          default:
            setLoadingData(false);
            return;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${currentView} view data`);
        }

        const data = await response.json();
        setViewData(data);
      } catch (err) {
        console.error(`Error fetching ${currentView} view data:`, err);
        setError(`Failed to load ${currentView} view data. Please try again.`);
      } finally {
        setLoadingData(false);
      }
    };

    fetchViewData();
  }, [
    selectedUploadId,
    currentView,
    selectedDirector,
    selectedMUM,
    selectedStore,
  ]);

  // Handle view change
  const handleViewChange = (view) => {
    setCurrentView(view);
    setViewData(null);
  };

  // Export to Excel
  const handleExport = () => {
    // TODO: Implement export functionality
    alert("Export functionality coming soon!");
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  // Get filtered MUMs based on selected director
  const getFilteredMUMs = () => {
    if (!selectedDirector) return filterOptions.mums;
    return filterOptions.mums.filter(
      (mum) => mum.director === selectedDirector
    );
  };

  // Get filtered stores based on selected MUM
  const getFilteredStores = () => {
    if (!selectedMUM) return filterOptions.stores;
    return filterOptions.stores.filter((store) => store.mum === selectedMUM);
  };

  if (loading) {
    return (
      <PageLayout
      >
        <div className="loading-container">Loading...</div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
      >
        <div className="error-container">{error}</div>
      </PageLayout>
    );
  }

  if (reportDates.length === 0) {
    return (
      <PageLayout
      >
        <div className="no-data-container">
          <p>No training reports available yet.</p>
          <p>Please upload training data to view results.</p>
        </div>
      </PageLayout>
    );
  }

  const selectedReport = reportDates.find(
    (r) => r.upload_id === selectedUploadId
  );
  console.log("📊 Selected Report:", selectedReport);

  return (
    <PageLayout
    >
      <div className="window-readiness-container">
        {/* Date Filter */}
        <div className="filters-section">
          <div className="date-filter">
            <label htmlFor="report-date">Report Date:</label>
            <select
              id="report-date"
              value={selectedUploadId || ""}
              onChange={(e) => setSelectedUploadId(parseInt(e.target.value))}
            >
              {reportDates.map((report) => (
                <option key={report.upload_id} value={report.upload_id}>
                  Report from{" "}
                  {new Date(report.upload_date).toLocaleDateString()} -{" "}
                  {report.location_count} locations
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button onClick={handleExport} className="btn-export">
              Export to Excel
            </button>
            <button onClick={handlePrint} className="btn-print">
              Print Report
            </button>
          </div>
        </div>

        {/* View Switcher */}
        <div className="view-switcher">
          <button
            className={`view-btn ${currentView === "vp" ? "active" : ""}`}
            onClick={() => handleViewChange("vp")}
          >
            VP
          </button>
          <button
            className={`view-btn ${currentView === "director" ? "active" : ""}`}
            onClick={() => handleViewChange("director")}
          >
            Director
          </button>
          <button
            className={`view-btn ${currentView === "mum" ? "active" : ""}`}
            onClick={() => handleViewChange("mum")}
          >
            MUM
          </button>
          <button
            className={`view-btn ${currentView === "store" ? "active" : ""}`}
            onClick={() => handleViewChange("store")}
          >
            Store
          </button>
        </div>

        {/* Role-Based Filters */}
        {currentView !== "vp" && (
          <div className="role-filters">
            {currentView === "director" && (
              <div className="filter-group">
                <label htmlFor="director-filter">Select Director:</label>
                <select
                  id="director-filter"
                  value={selectedDirector}
                  onChange={(e) => setSelectedDirector(e.target.value)}
                >
                  <option value="">-- Select Director --</option>
                  {filterOptions.directors.map((director, index) => (
                    <option key={index} value={director.director}>
                      {director.director}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {currentView === "mum" && (
              <>
                <div className="filter-group">
                  <label htmlFor="director-filter-mum">
                    Filter by Director (Optional):
                  </label>
                  <select
                    id="director-filter-mum"
                    value={selectedDirector}
                    onChange={(e) => {
                      setSelectedDirector(e.target.value);
                      setSelectedMUM("");
                    }}
                  >
                    <option value="">-- All Directors --</option>
                    {filterOptions.directors.map((director, index) => (
                      <option key={index} value={director.director}>
                        {director.director}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="mum-filter">Select MUM:</label>
                  <select
                    id="mum-filter"
                    value={selectedMUM}
                    onChange={(e) => setSelectedMUM(e.target.value)}
                  >
                    <option value="">-- Select MUM --</option>
                    {getFilteredMUMs().map((mum, index) => (
                      <option key={index} value={mum.mum}>
                        {mum.mum}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {currentView === "store" && (
              <>
                <div className="filter-group">
                  <label htmlFor="mum-filter-store">
                    Filter by MUM (Optional):
                  </label>
                  <select
                    id="mum-filter-store"
                    value={selectedMUM}
                    onChange={(e) => {
                      setSelectedMUM(e.target.value);
                      setSelectedStore("");
                    }}
                  >
                    <option value="">-- All MUMs --</option>
                    {filterOptions.mums.map((mum, index) => (
                      <option key={index} value={mum.mum}>
                        {mum.mum}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="store-filter">Select Store:</label>
                  <select
                    id="store-filter"
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                  >
                    <option value="">-- Select Store --</option>
                    {getFilteredStores().map((store, index) => (
                      <option key={index} value={store.location_pc}>
                        {store.training_location_name} - {store.city},{" "}
                        {store.state}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* View Content */}
        <div className="view-content">
          {loadingData ? (
            <div className="loading-data">Loading view data...</div>
          ) : (
            <>
              {currentView === "vp" && <VPView data={viewData} />}
              {currentView === "director" &&
                (selectedDirector ? (
                  <DirectorView data={viewData} />
                ) : (
                  <div className="select-prompt">
                    Please select a Director to view data
                  </div>
                ))}
              {currentView === "mum" &&
                (selectedMUM ? (
                  <MUMView data={viewData} />
                ) : (
                  <div className="select-prompt">
                    Please select a MUM to view data
                  </div>
                ))}
              {currentView === "store" &&
                (selectedStore ? (
                  <StoreView data={viewData} />
                ) : (
                  <div className="select-prompt">
                    Please select a Store to view data
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default WindowReadinessResults;
